import { Injectable } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { QuoteTemplateValues } from 'src/app/modules/shared/entities/quote-template-values.class';
import { QuoteDetails } from 'src/app/modules/shared/entities/quote-details.class';
import { Lead } from 'src/app/modules/crm/leads/entities/lead.class';
import { Customer } from '../../../shared/entities/customer.class';
import { DefaultRoute } from '../entities/default-route.class';
import { PaymentDetailsService } from './payment-details.service';

@Injectable({
  providedIn: 'root'
})
export class QuoteDetailsService {
  urls = {
    templateValues: '/api/quote/templatevalues',
    accountManagers: '/api/accountmanager/list',
    defaultRoutes: '/api/quote/defaultroutes',
    priceMatrixes: '/api/pricematrix/list'
  };
  scompDefaultName = 'SCOMP - Ship Complete';
  customersUrl = '/api/customer/list/';
  customersAndLeadsUrl = '/api/accountmanager/customersAndLeads/';
  getQuoteDetailsUrl = '/api/quote/get/';
  createQuoteUrl = '/api/quote/create';
  getCustomerQuoteDetailsUrl = '/api/quote/customerdetails/';
  getLeadQuoteDetailsUrl = '/api/quote/leaddetails/';
  editQuoteUrl = '/api/quote/edit';
  deleteQuoteUrl = '/api/quote/delete';
  completeQuoteUrl = '/api/quote/completeQuote';
  completeOrderUrl = '/api/order/create';
  approvePricingUrl = '/api/quote/approvePricing';
  updatePricingUrl = '/api/quote/updatePricing';
  validateOrderUrl = '/api/order/validate';
  restoreQuoteRevisionUrl = '/api/quote/restoreQuote';
  getQuoteRevisionsUrl = '/api/quote/quoteRevisions';
  getOrderRevisionUrl = '/api/order/orderRevision';
  removeRevisionUrl = '/api/quote/removeRevision';
  quoteNotificationUrl = '/api/quote/notification';
  private atpQuoteUrl = '/api/product/atpall';
  creditCardEligibilityUrl = '/api/payment/IsCreditCardEligible';
  quoteNumber$ = new Subject<string>();
  canEdit$ = new Subject<boolean>();
  isCompleted$ = new Subject<boolean>();
  paymentId$ = new Subject<any>();
  setCustomer$ = new Subject<Customer | Lead>();
  updateCustomer$ = new Subject<Customer>();
  quoteIsSaving$ = new Subject<boolean>();
  refreshCarriers$ = new Subject<boolean>();
  selectedCarrier$ = new Subject<any>();
  currencySign$ = new Subject<string>();
  priceMatrixOrCustomerChangedNotification$ = new Subject<boolean>();
  creditCardEligibility$ = new Subject<boolean>();
  setAccountManager$ = new Subject<string>();
  hideInactiveCustomers = true;

  summary = {
    quoteDetails: {}
  };

  cachedValues: any = [
    {
      CustomerType: null,
      FederalPriorityCode: null,
      LeadStatus: null,
      LnStatus: null,
      QuoteStatus: null,
      QuoteType: null,
      RecipientType: null,
    }
  ];
  pendingValues = false;
  callbackQueue = [];
  quoteRestored$ = new Subject<any>();

  constructor(
    private apiService: ApiService,
    private appStateService: AppStateService,
    private paymentDetailsService: PaymentDetailsService
  ) {
    // reset the AMs list at online change state, to prevent having wrong cached values
    this.appStateService.offlineStateToggle$.subscribe(() => this.cachedValues[1] = null);
  }

  getStaticValues(callback: (...args: any[]) => any): QuoteTemplateValues {
    const haveCachedData = this.cachedValues[0].FederalPriorityCode &&
      this.cachedValues[0].CustomerType &&
      this.cachedValues[0].LeadStatus &&
      this.cachedValues[0].LnStatus &&
      this.cachedValues[0].QuoteStatus &&
      this.cachedValues[0].QuoteType &&
      this.cachedValues[0].RecipientType &&
      this.cachedValues[1] &&
      this.cachedValues[2] &&
      this.cachedValues[3];
    const observableList = [];

    // if a call started but did not finish, queue the callback
    if (this.pendingValues) {
      this.callbackQueue.push(callback);
      return;
    }

    if (haveCachedData) {
      observableList.push(...[
        of(this.getCachedValues(0)),
        of(this.getCachedValues(1)),
        of(this.getCachedValues(2)),
        of(this.getCachedValues(3))
      ]);
    } else {
      observableList.push(...[
        this.apiService.get(this.urls.templateValues),
        this.apiService.get(this.urls.accountManagers),
        this.apiService.get(this.urls.defaultRoutes),
        this.apiService.get(this.urls.priceMatrixes)
      ]);
      this.pendingValues = true;
    }
    // queue the callback to be called below
    this.callbackQueue.push(callback);

    forkJoin(observableList).subscribe((resultArray: any) => {
      this.cachedValues = resultArray;
      // call all callbacks
      this.callbackQueue.forEach(cb => cb(this.getCachedValues()));
      this.callbackQueue = [];
      this.pendingValues = false;
    });
  }


  private getCachedValues(index?: number) {
    if (typeof index === 'undefined') {
      return [this.cachedValues[0], this.cachedValues[1].slice(), this.cachedValues[2].slice(), this.cachedValues[3].slice()];
    } else if (index) {
      return this.cachedValues[index].slice();
    } else {
      return this.cachedValues[0];
    }
  }

  /**
   * Returns the route for the "SCOM - Ship to complete" string
   * @param routeList the route list
   */
  getShipToCompleteSCOM(routeList: DefaultRoute[]) {
    const scom = routeList.filter(element => element.route === this.scompDefaultName);
    return scom.length ? scom[0] : null;
  }

  /**
   * Get Customers based on Account Manager EmployeeId
   * @param accountManagerEmployeeId - The Account Manager Employee Id
   * @param callback - The callback
   */
  getCustomers(accountManagerEmployeeId, callback: (...args: any[]) => any) {
    this.apiService.get(this.customersUrl + accountManagerEmployeeId)
      .subscribe(result => callback(result));
  }

  getCustomersAndLeads(accountManagerEmployeeId, hideInactiveCustomers, callback: (...args: any[]) => any) {
    this.apiService.get(this.customersAndLeadsUrl + accountManagerEmployeeId + '?hideInactiveCustomers=' + hideInactiveCustomers)
      .subscribe(result => callback(result));
  }

  /**
   * Get Quote Details
   * @param quoteId - The quote id
   * @param callback - The callback
   */
  getQuoteDetails(quoteId, callback: (...args: any[]) => any) {
    this.apiService.get(this.getQuoteDetailsUrl + quoteId)
      .subscribe(result => callback(result));
  }

  /**
   * Get Customer Quote Details
   * @param customerId - The customer id
   * @param callback - The callback
   */
  getCustomerQuoteDetails(customerId, callback: (...args: any[]) => any) {
    this.apiService.get(this.getCustomerQuoteDetailsUrl + customerId)
      .subscribe(result => callback(result));
  }

  /**
   * Get Lead Quote Details
   * @param leadId - The lead id
   * @param callback - The callback
   */
  getLeadQuoteDetails(leadId, callback: (...args: any[]) => any) {
    this.apiService.get(this.getLeadQuoteDetailsUrl + leadId)
      .subscribe(result => callback(result));
  }

  /**
   * Runs the callback on the result. Determines if it should get lead or customer data based on parameters.
   * We use a single method to simplify component implementation
   * @param clientType string, it will be considered to be customer unless explicitly set
   * @param clientId the id of the lead / customer
   * @param callback the callback function
   */
  getQuoteDetailsByClientType(clientType: string, clientId, callback: (...args: any[]) => any) {
    let apiUrl: string;
    if (clientType === 'lead') {
      apiUrl = this.getLeadQuoteDetailsUrl + clientId;
    } else {
      apiUrl = this.getCustomerQuoteDetailsUrl + clientId;
    }
    this.apiService.get(apiUrl).subscribe(result => callback(result));
  }

  /**
   * Creates a new quote
   * @param quoteDetails - The quote details
   * @param callback - The callback
   */
  createQuote(quoteDetails, callback: (...args: any[]) => any) {
    this.quoteIsSaving$.next(true);
    this.apiService.post(this.createQuoteUrl, this.createPayloadForQuote(quoteDetails))
      .subscribe(result => callback(result), () => this.quoteIsSaving$.next(false));
  }

  getAllAtp(partNumber: string, callback: (...args: any[]) => any) {
    this.apiService.post(this.atpQuoteUrl, { partNumberList: [partNumber] })
      .subscribe(atpValues => callback(atpValues), null);
  }

  /**
   * Edits/updates an existing quote
   * @param as quoteDetails
   * @param as errorCallback
   */
  editQuote(quoteDetails, callback: (...args: any[]) => any, errorCallback?: (...args: any[]) => any, ignoreCompleteAllCallback?) {
    if (!quoteDetails.priceMatrix) {
      return;
    }
    quoteDetails.priceMatrix = this.normalizePriceMatrixValues(quoteDetails.priceMatrix);
    this.apiService.queueRequest(
      this.editQuoteUrl,
      quoteDetails,
      callback,
      'post',
      () => {
        if (!ignoreCompleteAllCallback) {
          this.quoteIsSaving$.next(false);
        }
      },
      () => {
        if (errorCallback) {
          errorCallback();
        }
      });
  }

  /**
   * Deletes a quote
   * @param quoteId - The quote id
   * @param callback - The callback
   */
  deleteQuote(quoteId, callback: (...args: any[]) => any) {
    this.apiService.post(this.deleteQuoteUrl, { id: quoteId })
      .subscribe(result => callback(result));
  }

  /**
   * Returns true if the given quote id an existing quote, otherwise false
   * @param quoteId - The quote id to test
   */
  isExistingQuote(quoteId) {
    return quoteId && quoteId !== 'new';
  }

  /**
   * Returns the list of revisions for a specific quote
   * @param quote id as quoteId
   */
  getQuoteRevisions(quoteId) {
    return this.apiService.get(`${this.getQuoteRevisionsUrl}/${quoteId}`);
  }

  getOrderRevision(quoteId) {
    return this.apiService.get(`${this.getOrderRevisionUrl}/${quoteId}`);
  }

  private createPayloadForQuote(quoteDetails) {
    const payload: any = {};
    const isLead = quoteDetails.customer.isLead;
    const confidenceToCloseOptions: { [key: number]: string }
      = this.cachedValues[0].ConfidenceToClose;

    payload.customerId = isLead ? null : quoteDetails.customer.id;
    payload.leadId = isLead ? quoteDetails.customer.id : null;
    payload.federalPriorityCode = quoteDetails.federalPriorityCode;
    payload.priceMatrix = this.normalizePriceMatrixValues(quoteDetails.priceMatrix);
    payload.defaultRoute = isLead ? null : quoteDetails.defaultRoute;
    payload.quoteNotes = quoteDetails.quoteNotes;
    payload.blockOrder = quoteDetails.blockOrder;
    payload.shipToComplete = quoteDetails.shipToComplete;
    payload.createdForEmployeeId = quoteDetails.accountManager.employeeId;
    payload.quoteName = quoteDetails.quoteName;
    payload.quoteExpireDate = quoteDetails.quoteExpireDate;
    payload.priceAsCustomerId = quoteDetails.priceAs ? quoteDetails.priceAs.id : null;
    payload.atp = quoteDetails.atp;
    payload.confidenceToClose = Number(Object.keys(confidenceToCloseOptions).find(
      key => confidenceToCloseOptions[key] === quoteDetails.confidenceToClose));
    payload.estimatedCloseDate = quoteDetails.estimatedCloseDate;
    if (payload.confidenceToClose === 1) {
      payload.estimatedCloseDate = quoteDetails.quoteExpireDate;
    }
    return payload;
  }

  /**
   * This function makes sure that we send to be the priceMatrix object
   * When the value is not found in the dropdown list, we build the object
   * by ading an id to the value inserted by the user in the input.
   */
  private normalizePriceMatrixValues(priceMatrixValue) {
    if (!priceMatrixValue.id && !priceMatrixValue.priceList) {
      return { id: -1, priceList: priceMatrixValue };
    }
    return priceMatrixValue;
  }

  /**
   * Completes a quote
   * @param quoteId - the quote id by which the quote is completed
   * @returns Observable of API call response
   */
  completeQuote(quoteId) {
    const payload = {
      quoteId
    };
    return this.apiService.post(this.completeQuoteUrl, payload);
  }
  /** Send details about customer/lead
   * for determining the payment section layout
   */
  checkCreditCardEligibility(customerDetails) {
    // if customer
    if (customerDetails && customerDetails.id) {
      return this.apiService.post(this.creditCardEligibilityUrl, {
        id: customerDetails.id,
        isLead: customerDetails.isLead
      }).subscribe((result: boolean) =>
        this.creditCardEligibility$.next(result)
      );
    }
  }

  /**
   * Completes an order
   * @param quoteId - the quote id by which the order is completed
   * @returns as {Observable<any>} the api request
   */
  completeOrder(quoteId) {
    const payload = {
      quoteId
    };
    return this.apiService.post(this.completeOrderUrl, payload);
  }

  /**
   * Gives the user the ability to honor an expired quote
   * @param quoteId - the quote id
   * @returns as {Observable<any>} the api request
   */
  approveQuotePricing(quoteId) {
    const payload = {
      quoteId
    };
    return this.apiService.post(this.approvePricingUrl, payload);
  }

  /**
   * Restores the quote revision
   * @param quoteId - revision id
   * @returns Observable of API call response
   */
  restoreQuoteRevision(quoteId) {
    const payload = {
      quoteId
    };
    return this.apiService.post(this.restoreQuoteRevisionUrl, payload);
  }

  /**
   * Checks if a quote is expired
   * @param quote - the quote to be checked
   * @param now - the current time
   */
  checkQuoteIsExpired(quote: QuoteDetails, now) {
    if (quote.expireDate) {
      const quoteDate = new Date(quote.expireDate).getTime();
      quote.quoteIsExpired = now > quoteDate;
    } else {
      quote.quoteIsExpired = false;
    }
  }

  /**
   * Updates the products prices and notifications
   * @param quoteId - the quote id
   * @returns as {Observable<any>} the api request
   */
  updateQuotePricing(quoteId, notification) {
    const payload = {
      quoteId,
      notification
    };
    return this.apiService.post(this.updatePricingUrl, payload);
  }

  /**
   * Recalculates prices and do quote validations
   * @param as quote
   * @returns as {Observable<any>}
   */
  validateOrder(quote) {
    return this.apiService.post(this.validateOrderUrl, quote);
  }
  /**
   * Removes the isRevision attribute from the quote
   * @param quote - the quote
   * @returns as {Observable<any>} the api request
   */
  removeRevision(quote) {
    return this.apiService.post(this.removeRevisionUrl, quote);
  }

  /**
   * Checks if edit quote is in progress
   * @param as quoteId - the quote id
   * @returns an {Observable<any>}
   */
  checkIfEditNotificationInProgress(quoteId) {
    return this.apiService.get(`${this.quoteNotificationUrl}/${quoteId}`);
  }

  /**
   * Gets account managers list
   */
  getAccountManagersList() {
    return this.apiService.get(this.urls.accountManagers);
  }
}
